import Alpine from 'alpinejs';
import { formatDateISO, parseDate } from '../utils/dateUtils';

export default function registerCalendarSettingsReportsStore() {
  const existing = Alpine.store('calendarSettingsReportsStore');
  if (existing) return existing;

  return Alpine.store('calendarSettingsReportsStore', {
    calendarId: null,
    userPermission: null,
    bookingMode: 'RO',
    previousBookingMode: 'RO',
    invoicexpressEnabled: false,

    activeReportTab: 'performance',
    showPerformanceTab: true,
    performance: {
      loading: false,
      error: '',
      data: null,
      filters: {
        preset: 'last_3_months',
        date_from: '',
        date_to: '',
        comparison: 'off',
        booking_statuses: [
          'PENDING',
          'CONFIRMED_PART',
          'CONFIRMED',
          'CANCELLED',
        ],
      },
      activeMetrics: ['revenue'],
      revenueBreakdown: false,
      mixMetric: 'revenue',
      listingTypes: ['Accommodation', 'Tour', 'Package', 'Extra'],
      showAllListings: false,
    },
    performanceFlatpickrInstance: null,
    performanceAbortController: null,
    performanceChart: null,
    _performanceChartToken: 0,
    _performanceInitialized: false,
    noPartnerFilterValue: '__no_partner__',
    reportDatePresets: [
      ['this_month', 'Month'],
      ['last_month', 'Last mo'],
      ['last_30_days', '30 days'],
      ['last_3_months', '3 mo'],
      ['year_to_date', 'YTD'],
      ['last_year', 'Last yr'],
    ],
    financialReport: {
      loading: false,
      data: [],
      total_count: 0,
      total_amount: '0',
      pagination: {
        page: 1,
        page_size: 50,
        total_pages: 1,
        has_next: false,
        has_previous: false,
      },
      filters: {
        preset: 'last_3_months',
        date_from: '',
        date_to: '',
        partner_ids: [],
        booking_statuses: [],
        transaction_type_id: '',
      },
    },
    partnerReport: {
      loading: false,
      data: [],
      selectedBookings: [],
      total_count: 0,
      total_booking_amount: '0',
      total_partner_commission: '0',
      total_partner_invoice_amount: '0',
      pagination: {
        page: 1,
        page_size: 50,
        total_pages: 1,
        has_next: false,
        has_previous: false,
      },
      filters: {
        preset: 'last_3_months',
        date_from: '',
        date_to: '',
        date_filter_type: 'start_date',
        partner_ids: [],
        booking_statuses: [],
        invoiced: '',
        attribution_columns: [],
      },
      attributionColumnOptions: [
        { key: 'utm_source', label: 'UTM source' },
        { key: 'utm_medium', label: 'UTM medium' },
        { key: 'utm_campaign', label: 'UTM campaign' },
        { key: 'landing_path', label: 'Landing page' },
        { key: 'gclid', label: 'GCLID' },
        { key: 'gbraid', label: 'GBRAID' },
        { key: 'wbraid', label: 'WBRAID' },
        { key: 'utm_id', label: 'UTM campaign ID' },
        { key: 'utm_term', label: 'UTM term' },
        { key: 'utm_content', label: 'UTM content' },
      ],
      attribution_columns: [],
      bulkInvoiceTaskId: null,
      bundleTaskId: null,
      bundleInvoiceType: null,
      bundleStatus: null,
      bundlePollingInterval: null,
      invoiceType: 'simple',
    },
    customerReport: {
      loading: false,
      data: [],
      total_count: 0,
      custom_field_columns: [],
      filters: {
        preset: 'last_3_months',
        date_from: '',
        date_to: '',
        partner_ids: [],
        country: '',
        custom_field_ids: [],
      },
    },
    reportExport: {
      jobId: null,
      status: null,
      progressMessage: '',
      polling: false,
      error: null,
      fileName: null,
    },
    financialReportFlatpickrInstance: null,
    partnerReportFlatpickrInstance: null,
    customerReportFlatpickrInstance: null,
    reportsSTATUS_DISPLAY: {
      PENDING: 'Pending',
      CONFIRMED_PART: 'Deposit',
      CONFIRMED: 'Confirmed',
      CANCELLED: 'Cancelled',
    },
    get STATUS_DISPLAY() {
      return this.reportsSTATUS_DISPLAY;
    },

    init() {
      this.initializeReportsSettings();
      this.configurePerformanceVisibility();
    },

    configurePerformanceVisibility() {
      const container = document.getElementById('calendarSettingsContainer');
      const params = new URLSearchParams(window.location.search);
      const showTab = container?.dataset.showPerformanceTab === 'true';
      const requestedTab = params.get('tab');
      const otherTabs = ['partner', 'financial', 'customer'];
      this.showPerformanceTab = showTab;
      if (!showTab || (requestedTab && otherTabs.includes(requestedTab))) {
        this.activeReportTab = otherTabs.includes(requestedTab)
          ? requestedTab
          : 'partner';
        return;
      }
      this.activeReportTab = 'performance';
      this.initializePerformance();
    },

    initializeDefaultMultiFilters(
      defaultPartnerIds = [],
      defaultStatuses = [],
    ) {
      if (this.financialReport.filters.partner_ids.length === 0) {
        this.financialReport.filters.partner_ids = [...defaultPartnerIds];
      }
      if (this.partnerReport.filters.partner_ids.length === 0) {
        this.partnerReport.filters.partner_ids = [...defaultPartnerIds];
      }
      if (this.customerReport.filters.partner_ids.length === 0) {
        this.customerReport.filters.partner_ids = [...defaultPartnerIds];
      }
      if (this.financialReport.filters.booking_statuses.length === 0) {
        this.financialReport.filters.booking_statuses = [...defaultStatuses];
      }
      if (this.partnerReport.filters.booking_statuses.length === 0) {
        this.partnerReport.filters.booking_statuses = [...defaultStatuses];
      }
    },

    initializeSettingsContext() {
      const container = document.getElementById('calendarSettingsContainer');
      if (container) {
        this.calendarId = container.dataset.calendarId;
        this.userPermission = container.dataset.userPermission;
        this.bookingMode = container.dataset.bookingMode;
        this.previousBookingMode = container.dataset.bookingMode;
        this.invoicexpressEnabled =
          container.dataset.invoicexpressEnabled === 'true';
      }
    },

    initializeReportsSettings() {
      this.initializeSettingsContext();
      this.applyReportDatePreset(this.financialReport.filters, 'last_3_months');
      this.applyReportDatePreset(this.partnerReport.filters, 'last_3_months');
      this.applyReportDatePreset(this.customerReport.filters, 'last_3_months');
    },

    initializePerformance() {
      if (this._performanceInitialized) return;
      this._performanceInitialized = true;
      const today = new Date();
      const defaults = {
        preset: 'last_3_months',
        date_from: formatDateISO(
          this.performancePresetStart('last_3_months', today),
        ),
        date_to: formatDateISO(today),
        comparison: 'off',
        booking_statuses: [
          'PENDING',
          'CONFIRMED_PART',
          'CONFIRMED',
          'CANCELLED',
        ],
      };
      let saved = null;
      try {
        saved = JSON.parse(
          localStorage.getItem(`ht-performance:${this.calendarId}`),
        );
      } catch (error) {
        saved = null;
      }
      const savedFilters = saved?.filters || {};
      this.performance.filters = {
        ...defaults,
        ...savedFilters,
        date_from: savedFilters.date_from || defaults.date_from,
        date_to: savedFilters.date_to || defaults.date_to,
      };
      this.performance.activeMetrics = saved?.activeMetrics || ['revenue'];
      this.performance.revenueBreakdown = !!saved?.revenueBreakdown;
      this.performance.mixMetric = saved?.mixMetric || 'revenue';
      if (saved?.listingTypes?.length) {
        this.performance.listingTypes = saved.listingTypes;
        if (
          !saved.listingTypes.includes('Extra') &&
          saved.listingTypes.length === 3 &&
          ['Accommodation', 'Tour', 'Package'].every((type) =>
            saved.listingTypes.includes(type),
          )
        ) {
          this.performance.listingTypes = [...saved.listingTypes, 'Extra'];
        }
      }
      this.performance.loading = true;
      queueMicrotask(() => this.fetchPerformance());
    },

    savePerformancePreferences() {
      localStorage.setItem(
        `ht-performance:${this.calendarId}`,
        JSON.stringify({
          filters: this.performance.filters,
          activeMetrics: this.performance.activeMetrics,
          revenueBreakdown: this.performance.revenueBreakdown,
          listingTypes: this.performance.listingTypes,
          mixMetric: this.performance.mixMetric,
        }),
      );
    },

    openPerformanceTab() {
      this.activeReportTab = 'performance';
      if (!this._performanceInitialized) {
        this.initializePerformance();
        return;
      }
      if (!this.performance.data) {
        this.fetchPerformance();
        return;
      }
      this.schedulePerformanceChart();
    },

    schedulePerformanceChart() {
      const token = ++this._performanceChartToken;
      Alpine.nextTick(() => {
        requestAnimationFrame(() => this.renderPerformanceChart(token));
      });
    },

    reportFilters(reportName) {
      if (reportName === 'performance') return this.performance.filters;
      if (reportName === 'financial') return this.financialReport.filters;
      if (reportName === 'partner') return this.partnerReport.filters;
      return this.customerReport.filters;
    },

    reportDatePickerKey(reportName) {
      return {
        performance: 'performanceFlatpickrInstance',
        financial: 'financialReportFlatpickrInstance',
        partner: 'partnerReportFlatpickrInstance',
        customer: 'customerReportFlatpickrInstance',
      }[reportName];
    },

    performancePresetStart(preset, today = new Date()) {
      if (preset === 'this_month') {
        return new Date(today.getFullYear(), today.getMonth(), 1);
      }
      if (preset === 'last_month') {
        return new Date(today.getFullYear(), today.getMonth() - 1, 1);
      }
      if (preset === 'last_30_days') {
        const start = new Date(today);
        start.setDate(today.getDate() - 29);
        return start;
      }
      if (preset === 'last_3_months') {
        return new Date(
          today.getFullYear(),
          today.getMonth() - 3,
          today.getDate(),
        );
      }
      if (preset === 'last_year') {
        return new Date(today.getFullYear() - 1, 0, 1);
      }
      return new Date(today.getFullYear(), 0, 1);
    },

    reportPresetRange(preset, today = new Date()) {
      const valid = [
        'this_month',
        'last_month',
        'last_30_days',
        'last_3_months',
        'year_to_date',
        'last_year',
      ];
      if (!valid.includes(preset)) {
        preset = 'last_3_months';
      }
      let end = new Date(today);
      if (preset === 'last_month') {
        end = new Date(today.getFullYear(), today.getMonth(), 0);
      } else if (preset === 'last_year') {
        end = new Date(today.getFullYear() - 1, 11, 31);
      }
      return {
        preset,
        start: this.performancePresetStart(preset, today),
        end,
      };
    },

    applyReportDatePreset(filters, preset) {
      const range = this.reportPresetRange(preset);
      filters.preset = range.preset;
      filters.date_from = formatDateISO(range.start);
      filters.date_to = formatDateISO(range.end);
    },

    syncReportDatePicker(reportName) {
      const instance = this[this.reportDatePickerKey(reportName)];
      const filters = this.reportFilters(reportName);
      if (instance && filters.date_from && filters.date_to) {
        instance.setDate([filters.date_from, filters.date_to], false);
      }
    },

    setPerformancePreset(preset) {
      this.applyReportDatePreset(this.performance.filters, preset);
      this.syncReportDatePicker('performance');
      this.savePerformancePreferences();
      this.fetchPerformance();
    },

    setReportDatePreset(reportName, preset) {
      this.applyReportDatePreset(this.reportFilters(reportName), preset);
      this.syncReportDatePicker(reportName);
    },

    initReportDateRange(el, reportName, fetchOnChange = false) {
      if (typeof flatpickr === 'undefined' || !el) return;
      const instanceKey = this.reportDatePickerKey(reportName);
      this[instanceKey]?.destroy();
      const filters = this.reportFilters(reportName);
      this[instanceKey] = flatpickr(el, {
        mode: 'range',
        dateFormat: 'Y-m-d',
        showMonths: 2,
        defaultDate: [filters.date_from, filters.date_to].filter(Boolean),
        onChange: (dates) => {
          if (dates.length !== 2) return;
          filters.preset = 'custom';
          filters.date_from = formatDateISO(dates[0]);
          filters.date_to = formatDateISO(dates[1]);
          if (fetchOnChange) {
            this.savePerformancePreferences();
            this.fetchPerformance();
          }
        },
      });
    },

    initPerformanceDateRange(el) {
      this.initReportDateRange(el, 'performance', true);
    },

    initFinancialReportDateRange(el) {
      this.initReportDateRange(el, 'financial');
    },

    initPartnerReportDateRange(el) {
      this.initReportDateRange(el, 'partner');
    },

    initCustomerReportDateRange(el) {
      this.initReportDateRange(el, 'customer');
    },

    togglePerformanceStatus(value) {
      if (
        this.performance.filters.booking_statuses.length === 1 &&
        this.performance.filters.booking_statuses.includes(value)
      ) {
        return;
      }
      this.toggleArraySelection(
        this.performance.filters.booking_statuses,
        value,
      );
      this.savePerformancePreferences();
      this.fetchPerformance();
    },

    setPerformanceComparison(value) {
      this.performance.filters.comparison = value;
      this.savePerformancePreferences();
      this.fetchPerformance();
    },

    togglePerformanceMetric(value) {
      this.toggleArraySelection(this.performance.activeMetrics, value);
      if (!this.performance.activeMetrics.length) {
        this.performance.activeMetrics.push('revenue');
      }
      this.savePerformancePreferences();
      this.schedulePerformanceChart();
    },

    async fetchPerformance(forceRefresh = false) {
      this.performanceAbortController?.abort();
      const controller = new AbortController();
      this.performanceAbortController = controller;
      this.performance.loading = true;
      this.performance.error = '';
      const params = this.buildReportParams(this.performance.filters, [
        'booking_statuses',
      ]);
      params.delete('preset');
      if (forceRefresh) {
        params.set('refresh', '1');
      }
      try {
        const response = await fetch(
          `/reports/api/performance/?${params.toString()}`,
          { signal: controller.signal },
        );
        const result = await response.json();
        if (!response.ok) throw new Error(result.error || 'Unable to load');
        if (this.performanceAbortController !== controller) return;
        this.performance.data = result;
        this.schedulePerformanceChart();
      } catch (error) {
        if (error.name !== 'AbortError') this.performance.error = error.message;
      } finally {
        if (this.performanceAbortController === controller) {
          this.performance.loading = false;
        }
      }
    },

    performanceSummary(key) {
      return this.performance.data?.summary?.[key] ?? 0;
    },

    performanceComparison(key) {
      const current = Number(this.performanceSummary(key) || 0);
      const previous = Number(
        this.performance.data?.comparison?.summary?.[key] || 0,
      );
      if (this.performance.filters.comparison === 'off') return '';
      if (!previous && current) return 'New';
      if (!previous) return '0%';
      const change = ((current - previous) / previous) * 100;
      return `${change >= 0 ? '+' : ''}${change.toFixed(1)}%`;
    },

    performanceFormatValue(key, value) {
      if (['revenue', 'new_booking_value'].includes(key)) {
        return this.reportsFormatCurrency(
          value,
          this.performance.data?.currency || 'EUR',
        );
      }
      return Number(value || 0).toLocaleString();
    },

    performancePreviousSummary(key) {
      return this.performance.data?.comparison?.summary?.[key] ?? 0;
    },

    performancePreviousFormatted(key) {
      return this.performanceFormatValue(
        key,
        this.performancePreviousSummary(key),
      );
    },

    performanceCurrencyIcon() {
      const currency = this.performance.data?.currency;
      if (currency === 'USD') return 'fa-dollar-sign';
      if (currency === 'GBP') return 'fa-sterling-sign';
      return 'fa-euro-sign';
    },

    performanceOccupancy(kind) {
      const value = this.performance.data?.summary?.occupancy?.[kind];
      return value == null ? null : Number(value);
    },

    performanceOccupancyLabel(kind) {
      const value = this.performanceOccupancy(kind);
      return value == null ? '—' : `${value.toFixed(1)}%`;
    },

    performanceOccupancyPreviousLabel(kind) {
      const value =
        this.performance.data?.comparison?.summary?.occupancy?.[kind];
      if (value == null || value === '') return '—';
      return `${Number(value).toFixed(1)}%`;
    },

    performanceOccupancyRing(kind, track, fill) {
      const value = this.performanceOccupancy(kind);
      if (value == null) return `background:${track}`;
      return `background:conic-gradient(${fill} 0% ${value}%, ${track} ${value}% 100%)`;
    },

    performanceOccupancyComparison(kind) {
      const current = this.performanceOccupancy(kind);
      const previousRaw =
        this.performance.data?.comparison?.summary?.occupancy?.[kind];
      const previous = previousRaw == null ? null : Number(previousRaw);
      if (this.performance.filters.comparison === 'off') return '';
      if (current == null) return '—';
      if (!previous && current) return 'New';
      if (!previous) return '0%';
      const change = ((current - previous) / previous) * 100;
      return `${change >= 0 ? '+' : ''}${change.toFixed(1)}%`;
    },

    performanceSeriesValue(row, metric) {
      if (metric === 'occupancy') return row.occupancy?.accommodation;
      return Number(row?.[metric] || 0);
    },

    performanceComparisonValue(index, metric) {
      const row = this.performance.data?.comparison?.series?.[index];
      if (!row) return null;
      if (metric === 'occupancy') return row.occupancy?.accommodation;
      return Number(row?.[metric] || 0);
    },

    performanceSeriesMax(metric) {
      const rows = this.performance.data?.series || [];
      const values =
        metric === 'occupancy'
          ? rows.flatMap((row) => [
              row.occupancy?.accommodation || 0,
              row.occupancy?.tours || 0,
            ])
          : rows.map((row) => Number(row?.[metric] || 0));
      return Math.max(1, ...values);
    },

    performanceBarHeight(row, metric) {
      return `${Math.max(
        2,
        (this.performanceSeriesValue(row, metric) /
          this.performanceSeriesMax(metric)) *
          100,
      )}%`;
    },

    performanceCategoryHeight(row, category) {
      const value = Number(row.categories?.[category] || 0);
      return `${Math.max(
        value ? 1 : 0,
        (value / this.performanceSeriesMax('revenue')) * 100,
      )}%`;
    },

    performanceDonutRows(kind) {
      const rows = this.performance.data?.[kind] || [];
      return rows
        .map((row) => ({
          ...row,
          value: Number(row[this.performance.mixMetric] || 0),
        }))
        .filter((row) => row.value > 0)
        .sort((a, b) => b.value - a.value);
    },

    performanceDonutStyle(kind) {
      const colors = [
        '#a8d5ba',
        '#a8c7e8',
        '#e8c6a8',
        '#d2b7e5',
        '#f2b8c6',
        '#b8dfdc',
        '#e8dda8',
      ];
      const rows = this.performanceDonutRows(kind);
      const total = rows.reduce((sum, row) => sum + row.value, 0);
      if (!total) return 'background:#eef2ef';
      let cursor = 0;
      const stops = rows.map((row, index) => {
        const start = cursor;
        cursor += (row.value / total) * 100;
        return `${colors[index % colors.length]} ${start}% ${cursor}%`;
      });
      return `background:conic-gradient(${stops.join(',')})`;
    },

    performanceDonutColor(index) {
      return [
        '#a8d5ba',
        '#a8c7e8',
        '#e8c6a8',
        '#d2b7e5',
        '#f2b8c6',
        '#b8dfdc',
        '#e8dda8',
      ][index % 7];
    },

    performanceDonutPercent(kind, value) {
      const total = this.performanceDonutRows(kind).reduce(
        (sum, row) => sum + row.value,
        0,
      );
      return total ? `${((value / total) * 100).toFixed(1)}%` : '0%';
    },

    visiblePerformanceListings() {
      const filtered = (this.performance.data?.listings || []).filter((row) =>
        this.performance.listingTypes.includes(row.type),
      );
      return this.performance.showAllListings ? filtered : filtered.slice(0, 8);
    },

    togglePerformanceListingType(type) {
      this.toggleArraySelection(this.performance.listingTypes, type);
      if (!this.performance.listingTypes.length) {
        this.performance.listingTypes.push(type);
      }
      this.savePerformancePreferences();
    },

    isCanceledOnly() {
      const statuses = this.performance.filters.booking_statuses || [];
      return statuses.length === 1 && statuses.includes('CANCELLED');
    },

    performanceHasSeriesValues() {
      return (this.performance.data?.series || []).some(
        (row) =>
          Number(row.revenue) ||
          Number(row.bookings) ||
          Number(row.new_booking_value) ||
          Number(row.new_bookings),
      );
    },

    performanceComparisonLabel() {
      const comparison = this.performance.data?.comparison;
      if (!comparison?.start || this.performance.filters.comparison === 'off') {
        return '';
      }
      return `vs ${this.formatDate(comparison.start)} – ${this.formatDate(
        comparison.end,
      )}`;
    },

    performanceDataAgeMs() {
      const raw = this.performance.data?.generated_at;
      if (!raw) return null;
      const then = Date.parse(raw);
      if (Number.isNaN(then)) return null;
      return Date.now() - then;
    },

    performanceDataIsStale() {
      const ms = this.performanceDataAgeMs();
      return ms != null && ms >= 2 * 24 * 60 * 60 * 1000;
    },

    performanceDataAgeLabel() {
      const ms = this.performanceDataAgeMs();
      if (ms == null) return '';
      const minutes = Math.max(0, Math.floor(ms / 60000));
      if (minutes < 1) return 'Updated just now';
      if (minutes < 60) {
        return `Updated ${minutes} minute${minutes === 1 ? '' : 's'} ago`;
      }
      const hours = Math.floor(minutes / 60);
      if (!this.performanceDataIsStale()) {
        return `Updated ${hours} hour${hours === 1 ? '' : 's'} ago`;
      }
      const days = Math.floor(hours / 24);
      return (
        `This view is from ${days} day${days === 1 ? '' : 's'} ago. ` +
        'Refresh for the latest bookings.'
      );
    },

    performanceMetricHint(key) {
      return {
        revenue:
          'Booking value from selected statuses, attributed to service dates.',
        bookings:
          'Eligible non-canceled bookings counted once on their arrival date.',
        new_booking_value:
          'Value of bookings created in this range, regardless of when the stay is.',
        new_bookings:
          'Bookings created in this range, counted on the date they were made.',
        occupancy_rooms:
          'Share of sellable nights occupied, including linked-product blocks.',
        occupancy_tours:
          'Share of sellable tour units occupied, including linked-product blocks.',
      }[key];
    },

    listingTypeBadge(type) {
      if (type === 'Package') return '#eadff2';
      if (type === 'Tour') return '#dce9f5';
      if (type === 'Extra') return '#f6e7d8';
      return '#dcefe3';
    },

    loadExternalScript(src, dataAttr, getGlobal) {
      const existingGlobal = getGlobal();
      if (existingGlobal) return Promise.resolve(existingGlobal);
      return new Promise((resolve, reject) => {
        const existing = document.querySelector(`script[${dataAttr}]`);
        if (existing) {
          const loaded = getGlobal();
          if (loaded) {
            resolve(loaded);
            return;
          }
          existing.addEventListener('load', () => resolve(getGlobal()), {
            once: true,
          });
          existing.addEventListener('error', reject, { once: true });
          return;
        }
        const script = document.createElement('script');
        script.src = src;
        script.setAttribute(dataAttr, 'true');
        script.onload = () => resolve(getGlobal());
        script.onerror = reject;
        document.head.appendChild(script);
      });
    },

    async loadChartJs() {
      return this.loadExternalScript(
        'https://cdn.jsdelivr.net/npm/chart.js@4.4.6/dist/chart.umd.min.js',
        'data-performance-chart',
        () => window.Chart,
      );
    },

    async loadPerformancePdfLibs() {
      await this.loadExternalScript(
        'https://cdn.jsdelivr.net/npm/html2canvas@1.4.1/dist/html2canvas.min.js',
        'data-performance-html2canvas',
        () => window.html2canvas,
      );
      await this.loadExternalScript(
        'https://cdn.jsdelivr.net/npm/jspdf@2.5.2/dist/jspdf.umd.min.js',
        'data-performance-jspdf',
        () => window.jspdf?.jsPDF,
      );
      if (!window.html2canvas || !window.jspdf?.jsPDF) {
        throw new Error('PDF libraries failed to load');
      }
    },

    destroyPerformanceChart() {
      if (!this.performanceChart) return;
      this.performanceChart.destroy();
      this.performanceChart = null;
    },

    async renderPerformanceChart(
      token = ++this._performanceChartToken,
      attempt = 0,
    ) {
      if (token !== this._performanceChartToken) return;
      const canvas = document.getElementById('performanceChart');
      if (!canvas || !this.performance.data) {
        this.destroyPerformanceChart();
        return;
      }
      if (!this.performanceHasSeriesValues()) {
        this.destroyPerformanceChart();
        return;
      }
      const context = canvas.getContext('2d');
      if (!context || canvas.clientWidth === 0 || canvas.clientHeight === 0) {
        if (attempt < 10) {
          requestAnimationFrame(() => {
            if (token === this._performanceChartToken) {
              this.renderPerformanceChart(token, attempt + 1);
            }
          });
        }
        return;
      }
      try {
        const Chart = await this.loadChartJs();
        if (token !== this._performanceChartToken) return;
        this.destroyPerformanceChart();
        if (token !== this._performanceChartToken) return;
        if (!canvas.isConnected || !canvas.getContext('2d')) return;
        const rows = this.performance.data.series || [];
        const comparisonRows = this.performance.data.comparison?.series || [];
        const currency = this.performance.data.currency || 'EUR';
        const showOccupancy =
          this.performance.activeMetrics.includes('occupancy');
        const showBreakdown = this.performance.revenueBreakdown;
        const showRevenue =
          this.performance.activeMetrics.includes('revenue') || showBreakdown;
        const showMoney =
          showRevenue ||
          this.performance.activeMetrics.includes('new_booking_value');
        const showCounts =
          this.performance.activeMetrics.includes('bookings') ||
          this.performance.activeMetrics.includes('new_bookings');
        const colors = {
          revenue: '#9fd4b3',
          bookings: '#9fc3e6',
          new_booking_value: '#d4ae7a',
          new_bookings: '#d2b7e5',
          accommodation: '#9fd4b3',
          tours: '#9fc3e6',
          extras: '#e4c29c',
          fees: '#d2b7e5',
        };
        const datasets = [];
        if (showBreakdown) {
          ['Accommodation', 'Tours', 'Extras', 'Cancellation fees'].forEach(
            (category, index) => {
              const color = [
                colors.accommodation,
                colors.tours,
                colors.extras,
                colors.fees,
              ][index];
              datasets.push({
                type: 'bar',
                label: category,
                data: rows.map((row) =>
                  Number(row.categories?.[category] || 0),
                ),
                backgroundColor: color,
                stack: 'revenue',
                yAxisID: 'money',
                order: 2,
                borderRadius: 6,
                maxBarThickness: 28,
              });
            },
          );
        } else if (this.performance.activeMetrics.includes('revenue')) {
          datasets.push({
            type: 'bar',
            label: 'Revenue',
            data: rows.map((row) => Number(row.revenue || 0)),
            backgroundColor: colors.revenue,
            stack: 'revenue',
            yAxisID: 'money',
            order: 2,
            borderRadius: 8,
            maxBarThickness: 28,
          });
        }
        if (this.performance.activeMetrics.includes('new_booking_value')) {
          datasets.push({
            type: 'bar',
            label: 'Value booked',
            data: rows.map((row) => Number(row.new_booking_value || 0)),
            backgroundColor: colors.new_booking_value,
            stack: 'booked',
            yAxisID: 'money',
            order: 2,
            borderRadius: 8,
            maxBarThickness: 16,
          });
        }
        if (this.performance.activeMetrics.includes('bookings')) {
          datasets.push({
            type: 'line',
            label: 'Bookings',
            data: rows.map((row) => Number(row.bookings || 0)),
            borderColor: colors.bookings,
            backgroundColor: colors.bookings,
            yAxisID: 'count',
            order: 1,
            tension: 0.35,
            pointRadius: 3,
          });
        }
        if (this.performance.activeMetrics.includes('new_bookings')) {
          datasets.push({
            type: 'line',
            label: 'Bookings made',
            data: rows.map((row) => Number(row.new_bookings || 0)),
            borderColor: colors.new_bookings,
            backgroundColor: colors.new_bookings,
            yAxisID: 'count',
            order: 1,
            tension: 0.35,
            pointRadius: 3,
          });
        }
        if (showOccupancy) {
          datasets.push({
            type: 'line',
            label: 'Stays occupancy',
            data: rows.map((row) => row.occupancy?.accommodation),
            borderColor: '#7eb89a',
            backgroundColor: '#7eb89a',
            yAxisID: 'occupancy',
            order: 1,
            tension: 0.35,
            pointRadius: 3,
            spanGaps: true,
          });
          datasets.push({
            type: 'line',
            label: 'Tour occupancy',
            data: rows.map((row) => row.occupancy?.tours),
            borderColor: '#8fb4d9',
            backgroundColor: '#8fb4d9',
            yAxisID: 'occupancy',
            order: 1,
            tension: 0.35,
            pointRadius: 3,
            spanGaps: true,
          });
        }
        if (this.performance.filters.comparison !== 'off') {
          const comparisonLine = (label, key, color) => ({
            type: 'line',
            label,
            data: rows.map((_row, index) =>
              Number(comparisonRows[index]?.[key] || 0),
            ),
            borderColor: color,
            backgroundColor: 'transparent',
            borderDash: [6, 4],
            borderWidth: 2,
            fill: false,
            pointStyle: 'line',
            yAxisID: 'money',
            order: 1,
            pointRadius: 0,
            tension: 0.25,
          });
          if (this.performance.activeMetrics.includes('revenue')) {
            datasets.push(
              comparisonLine('Comparison revenue', 'revenue', '#8a958e'),
            );
          }
          if (this.performance.activeMetrics.includes('new_booking_value')) {
            datasets.push(
              comparisonLine(
                'Comparison value booked',
                'new_booking_value',
                '#d4ae7a',
              ),
            );
          }
        }
        const scales = {
          x: {
            grid: { display: false },
            ticks: { color: '#708078', maxRotation: 0, autoSkip: true },
          },
        };
        if (showMoney) {
          scales.money = {
            type: 'linear',
            axis: 'y',
            position: 'left',
            beginAtZero: true,
            grid: { color: '#eef3f0' },
            ticks: {
              color: '#708078',
              callback: (value) =>
                this.reportsFormatCurrency(value, currency).replace('.00', ''),
            },
          };
        }
        if (showCounts) {
          scales.count = {
            type: 'linear',
            axis: 'y',
            position: showMoney || showOccupancy ? 'right' : 'left',
            beginAtZero: true,
            grid: {
              color: '#eef3f0',
              drawOnChartArea: !showMoney && !showOccupancy,
            },
            ticks: { color: '#708078', precision: 0 },
          };
        }
        if (showOccupancy) {
          scales.occupancy = {
            type: 'linear',
            axis: 'y',
            position: showMoney ? 'right' : 'left',
            min: 0,
            max: 100,
            grid: {
              color: '#eef3f0',
              drawOnChartArea: !showMoney,
            },
            ticks: { color: '#708078', callback: (value) => `${value}%` },
          };
        }
        this.performanceChart = new Chart(canvas, {
          type: 'bar',
          data: {
            labels: rows.map((row) =>
              row.incomplete ? `${row.label}*` : row.label,
            ),
            datasets,
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            animation: false,
            interaction: { mode: 'index', intersect: false },
            plugins: {
              legend: {
                position: 'bottom',
                labels: {
                  boxWidth: 18,
                  boxHeight: 8,
                  color: '#526159',
                  usePointStyle: true,
                  generateLabels(chart) {
                    const items =
                      Chart.defaults.plugins.legend.labels.generateLabels(
                        chart,
                      );
                    items.forEach((item) => {
                      if (item.text.startsWith('Comparison')) {
                        item.pointStyle = 'line';
                        item.lineDash = [6, 4];
                        item.lineWidth = 2;
                        item.fillStyle = 'transparent';
                      }
                    });
                    return items;
                  },
                },
              },
              tooltip: {
                callbacks: {
                  afterTitle: (items) => {
                    const row = rows[items[0]?.dataIndex];
                    return row?.incomplete ? 'Incomplete period' : '';
                  },
                  label: (item) => {
                    const label = item.dataset.label || '';
                    if (label.toLowerCase().includes('occupancy')) {
                      return `${label}: ${item.parsed.y ?? '—'}%`;
                    }
                    if (['Bookings', 'Bookings made'].includes(label)) {
                      return `${label}: ${item.parsed.y}`;
                    }
                    return `${label}: ${this.reportsFormatCurrency(
                      item.parsed.y,
                      currency,
                    )}`;
                  },
                },
              },
            },
            scales,
          },
        });
      } catch (error) {
        console.warn('Performance chart could not be rendered', error);
      }
    },

    getCsrfToken() {
      const input = document.querySelector('[name=csrfmiddlewaretoken]');
      return input ? input.value : '';
    },

    async fetchFinancialReport(resetPage = false) {
      this.financialReport.loading = true;
      try {
        if (resetPage) {
          this.financialReport.pagination.page = 1;
        }
        const params = this.buildReportParams(this.financialReport.filters, [
          'partner_ids',
          'booking_statuses',
        ]);
        params.delete('preset');
        params.set('page', this.financialReport.pagination.page);
        params.set('page_size', this.financialReport.pagination.page_size);
        const response = await fetch(
          `/reports/api/financial-report/?${params.toString()}`,
        );
        const result = await response.json();
        if (response.ok) {
          this.financialReport.data = result.transactions || [];
          this.financialReport.total_count = result.total_count || 0;
          this.financialReport.total_amount = result.total_amount || '0';
          this.financialReport.pagination.page = result.page || 1;
          this.financialReport.pagination.page_size =
            result.page_size || this.financialReport.pagination.page_size;
          this.financialReport.pagination.total_pages = result.total_pages || 1;
          this.financialReport.pagination.has_next = !!result.has_next;
          this.financialReport.pagination.has_previous = !!result.has_previous;
        } else {
          alert(
            'Error: ' + (result.error || 'Failed to fetch transaction report'),
          );
        }
      } catch (error) {
        alert('Error fetching transaction report: ' + error.message);
      } finally {
        this.financialReport.loading = false;
      }
    },

    async fetchPartnerReport(resetPage = false) {
      this.partnerReport.loading = true;
      try {
        if (resetPage) {
          this.partnerReport.pagination.page = 1;
          this.partnerReport.selectedBookings = [];
        }
        const params = this.buildReportParams(this.partnerReport.filters, [
          'partner_ids',
          'booking_statuses',
          'attribution_columns',
        ]);
        params.delete('preset');
        params.set('page', this.partnerReport.pagination.page);
        params.set('page_size', this.partnerReport.pagination.page_size);
        const response = await fetch(
          `/reports/api/partner-report/?${params.toString()}`,
        );
        const result = await response.json();
        if (response.ok) {
          this.partnerReport.data = result.bookings || [];
          this.partnerReport.attribution_columns =
            result.attribution_columns || [];
          this.partnerReport.total_count = result.total_count || 0;
          this.partnerReport.total_booking_amount =
            result.total_booking_amount || '0.00';
          this.partnerReport.total_partner_commission =
            result.total_partner_commission || '0.00';
          this.partnerReport.total_partner_invoice_amount =
            result.total_partner_invoice_amount || '0.00';
          this.partnerReport.pagination.page = result.page || 1;
          this.partnerReport.pagination.page_size =
            result.page_size || this.partnerReport.pagination.page_size;
          this.partnerReport.pagination.total_pages = result.total_pages || 1;
          this.partnerReport.pagination.has_next = !!result.has_next;
          this.partnerReport.pagination.has_previous = !!result.has_previous;
        } else {
          alert('Error: ' + (result.error || 'Failed to fetch booking report'));
        }
      } catch (error) {
        alert('Error fetching booking report: ' + error.message);
      } finally {
        this.partnerReport.loading = false;
      }
    },

    async startReportExport(reportType, format) {
      this.reportExport.jobId = null;
      this.reportExport.status = 'starting';
      this.reportExport.progressMessage = 'Starting export...';
      this.reportExport.error = null;
      this.reportExport.fileName = null;

      let filters;
      if (reportType === 'financial') {
        filters = { ...this.financialReport.filters };
      } else if (reportType === 'customer') {
        filters = { ...this.customerReport.filters };
      } else if (reportType === 'performance') {
        filters = {
          ...this.performance.filters,
          listing_types: [...this.performance.listingTypes],
        };
      } else {
        filters = { ...this.partnerReport.filters };
      }

      try {
        const response = await fetch('/reports/api/start-export/', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': this.getCsrfToken(),
          },
          body: JSON.stringify({
            report_type: reportType,
            format,
            filters,
          }),
        });
        const result = await response.json();
        if (response.ok && result.job_id) {
          this.reportExport.jobId = result.job_id;
          this.reportExport.status = 'pending';
          this.pollReportExport();
        } else {
          this.reportExport.status = null;
          this.reportExport.progressMessage = '';
          alert('Error: ' + (result.error || 'Failed to start export'));
        }
      } catch (error) {
        this.reportExport.status = null;
        this.reportExport.progressMessage = '';
        alert('Error starting export: ' + error.message);
      }
    },

    async pollReportExport() {
      if (!this.reportExport.jobId) return;
      this.reportExport.polling = true;
      const check = async () => {
        try {
          const resp = await fetch(
            `/reports/api/export-status/${this.reportExport.jobId}/`,
          );
          const data = await resp.json();
          this.reportExport.status = data.status;
          this.reportExport.progressMessage = data.progress_message || '';
          if (data.status === 'completed') {
            this.reportExport.fileName = data.file_name;
            this.reportExport.polling = false;
            window.location.href = `/reports/api/export-download/${this.reportExport.jobId}/`;
            return;
          }
          if (data.status === 'failed') {
            this.reportExport.error = data.error || 'Export failed';
            this.reportExport.polling = false;
            alert('Export failed: ' + (data.error || 'Unknown error'));
            return;
          }
          setTimeout(() => check(), 1000);
        } catch (err) {
          this.reportExport.polling = false;
          this.reportExport.status = null;
          alert('Error checking export status: ' + err.message);
        }
      };
      check();
    },

    async exportPerformanceReport() {
      const root = document.getElementById('performanceDashboard');
      if (!root || !this.performance.data) {
        alert('Load the dashboard before exporting.');
        return;
      }
      this.reportExport.polling = true;
      this.reportExport.progressMessage = 'Capturing dashboard…';
      this.reportExport.error = null;
      try {
        await this.loadPerformancePdfLibs();
        if (this.performanceHasSeriesValues()) {
          await this.renderPerformanceChart(this._performanceChartToken);
        }
        await new Promise((resolve) => {
          requestAnimationFrame(() => requestAnimationFrame(resolve));
        });
        const canvas = await window.html2canvas(root, {
          scale: 2,
          useCORS: true,
          backgroundColor: '#ffffff',
          logging: false,
          ignoreElements: (element) =>
            Boolean(element.closest?.('.performance-export-actions')),
        });
        const image = canvas.toDataURL('image/png');
        const JsPDF = window.jspdf.jsPDF;
        const pdf = new JsPDF({
          orientation: 'portrait',
          unit: 'mm',
          format: 'a4',
        });
        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();
        const margin = 8;
        const usableWidth = pageWidth - margin * 2;
        const usableHeight = pageHeight - margin * 2;
        const imageHeight = (canvas.height * usableWidth) / canvas.width;
        let heightLeft = imageHeight;
        let position = margin;
        pdf.addImage(image, 'PNG', margin, position, usableWidth, imageHeight);
        heightLeft -= usableHeight;
        while (heightLeft > 0) {
          position = margin - (imageHeight - heightLeft);
          pdf.addPage();
          pdf.addImage(
            image,
            'PNG',
            margin,
            position,
            usableWidth,
            imageHeight,
          );
          heightLeft -= usableHeight;
        }
        const from = this.performance.filters.date_from || 'start';
        const to = this.performance.filters.date_to || 'end';
        pdf.save(`performance-dashboard-${from}-to-${to}.pdf`);
      } catch (error) {
        console.warn(
          'Performance snapshot PDF failed, opening print dialog',
          error,
        );
        window.print();
      } finally {
        this.reportExport.polling = false;
        this.reportExport.progressMessage = '';
      }
    },

    exportFinancialReport(format) {
      this.startReportExport('financial', format);
    },

    exportPartnerReport(format) {
      this.startReportExport('partner', format);
    },

    async fetchCustomerReport() {
      this.customerReport.loading = true;
      try {
        const params = new URLSearchParams();
        params.set('date_from', this.customerReport.filters.date_from);
        params.set('date_to', this.customerReport.filters.date_to);
        (this.customerReport.filters.partner_ids || []).forEach((id) =>
          params.append('partner_ids', id),
        );
        if (this.customerReport.filters.country)
          params.set('country', this.customerReport.filters.country);
        (this.customerReport.filters.custom_field_ids || []).forEach((id) =>
          params.append('custom_field_ids', id),
        );
        const response = await fetch(
          `/reports/api/customer-report/?${params.toString()}`,
        );
        const result = await response.json();
        if (response.ok) {
          this.customerReport.data = result.customers || [];
          this.customerReport.total_count = result.total_count || 0;
          this.customerReport.custom_field_columns =
            result.custom_field_columns || [];
        } else {
          alert(
            'Error: ' + (result.error || 'Failed to fetch customer report'),
          );
        }
      } catch (error) {
        alert('Error fetching customer report: ' + error.message);
      } finally {
        this.customerReport.loading = false;
      }
    },

    exportCustomerReport(format) {
      this.startReportExport('customer', format);
    },

    toggleCustomField(fieldId) {
      const idx = (this.customerReport.filters.custom_field_ids || []).indexOf(
        fieldId,
      );
      if (idx > -1) {
        this.customerReport.filters.custom_field_ids.splice(idx, 1);
      } else {
        (this.customerReport.filters.custom_field_ids =
          this.customerReport.filters.custom_field_ids || []).push(fieldId);
      }
    },

    toggleBookingSelection(bookingId) {
      const booking = this.partnerReport.data.find((b) => b.id === bookingId);
      if (booking && booking.is_invoiced) return;
      const index = this.partnerReport.selectedBookings.indexOf(bookingId);
      if (index > -1) {
        this.partnerReport.selectedBookings.splice(index, 1);
      } else {
        this.partnerReport.selectedBookings.push(bookingId);
      }
    },

    areAllPageBookingsSelected() {
      const pageBookingIds = this.partnerReport.data
        .filter((b) => !b.is_invoiced)
        .map((b) => b.id);
      if (pageBookingIds.length === 0) return false;
      return pageBookingIds.every((id) =>
        this.partnerReport.selectedBookings.includes(id),
      );
    },

    buildReportParams(filters, arrayKeys = []) {
      const params = new URLSearchParams();
      Object.entries(filters || {}).forEach(([key, value]) => {
        if (arrayKeys.includes(key)) {
          (value || []).forEach((item) => {
            if (item !== '' && item !== null && item !== undefined) {
              params.append(key, item);
            }
          });
          return;
        }

        if (value !== '' && value !== null && value !== undefined) {
          params.set(key, value);
        }
      });
      return params;
    },

    toggleArraySelection(targetArray, value) {
      const list = Array.isArray(targetArray) ? targetArray : [];
      const idx = list.indexOf(value);
      if (idx > -1) {
        list.splice(idx, 1);
      } else {
        list.push(value);
      }
    },

    isArraySelected(targetArray, value) {
      return Array.isArray(targetArray) && targetArray.includes(value);
    },

    clearArraySelections(targetArray) {
      if (Array.isArray(targetArray)) {
        targetArray.splice(0, targetArray.length);
      }
    },

    selectionCountLabel(targetArray, emptyLabel) {
      const count = Array.isArray(targetArray) ? targetArray.length : 0;
      if (!count) return emptyLabel;
      return `${count} selected`;
    },

    selectAllBookings() {
      const pageBookingIds = this.partnerReport.data
        .filter((b) => !b.is_invoiced)
        .map((b) => b.id);
      const selected = new Set(this.partnerReport.selectedBookings);
      pageBookingIds.forEach((id) => selected.add(id));
      this.partnerReport.selectedBookings = Array.from(selected);
    },

    toggleSelectAll() {
      const pageBookingIds = this.partnerReport.data
        .filter((b) => !b.is_invoiced)
        .map((b) => b.id);
      const allSelected = this.areAllPageBookingsSelected();
      if (allSelected) {
        this.partnerReport.selectedBookings =
          this.partnerReport.selectedBookings.filter(
            (id) => !pageBookingIds.includes(id),
          );
        return;
      }

      const selected = new Set(this.partnerReport.selectedBookings);
      pageBookingIds.forEach((id) => selected.add(id));
      this.partnerReport.selectedBookings = Array.from(selected);
    },

    clearBookingSelection() {
      this.partnerReport.selectedBookings = [];
    },

    changeFinancialReportPage(delta) {
      const nextPage = this.financialReport.pagination.page + delta;
      if (
        nextPage < 1 ||
        nextPage > (this.financialReport.pagination.total_pages || 1)
      ) {
        return;
      }
      this.financialReport.pagination.page = nextPage;
      this.fetchFinancialReport(false);
    },

    changePartnerReportPage(delta) {
      const nextPage = this.partnerReport.pagination.page + delta;
      if (
        nextPage < 1 ||
        nextPage > (this.partnerReport.pagination.total_pages || 1)
      ) {
        return;
      }
      this.partnerReport.pagination.page = nextPage;
      this.fetchPartnerReport(false);
    },

    getSelectedPartner() {
      const selectedPartnerIds = this.partnerReport.filters.partner_ids || [];
      if (selectedPartnerIds.length !== 1) return null;

      const selectedPartnerId = selectedPartnerIds[0];

      const firstBooking = this.partnerReport.data.find(
        (b) => b.partner_id === selectedPartnerId,
      );
      if (firstBooking) {
        return {
          id: selectedPartnerId,
          name: firstBooking.partner_name || 'Unknown Partner',
          invoicing_mode: firstBooking.partner_invoicing_mode || null,
          bulk_partner_invoices_enabled: Boolean(
            firstBooking.partner_bulk_partner_invoices_enabled,
          ),
        };
      }

      const partnerSelects = document.querySelectorAll('select.form-select');
      for (let i = 0; i < partnerSelects.length; i += 1) {
        const sel = partnerSelects[i];
        if (
          sel.getAttribute('x-model') === 'partnerReport.filters.partner_ids' &&
          sel.selectedIndex > 0
        ) {
          return {
            id: selectedPartnerId,
            name: sel.options[sel.selectedIndex].text,
            invoicing_mode: null,
            bulk_partner_invoices_enabled: false,
          };
        }
      }

      return {
        id: selectedPartnerId,
        name: 'Selected Partner',
        invoicing_mode: null,
        bulk_partner_invoices_enabled: false,
      };
    },

    getPartnerSourceCode() {
      return this.partnerReport.data.length > 0
        ? this.partnerReport.data[0].partner_booking_source_code || ''
        : '';
    },

    hasPartnerInvoicingActive() {
      const selectedPartnerIds = this.partnerReport.filters.partner_ids || [];
      if (selectedPartnerIds.length !== 1) {
        return false;
      }
      if (selectedPartnerIds[0] === this.noPartnerFilterValue) {
        return false;
      }
      const partner = this.getSelectedPartner();
      return partner && partner.invoicing_mode === 'PARTNER';
    },

    async createBulkInvoice() {
      const selectedPartnerIds = this.partnerReport.filters.partner_ids || [];
      if (selectedPartnerIds.length !== 1) {
        alert('Please select exactly one partner first');
        return;
      }
      if (selectedPartnerIds[0] === this.noPartnerFilterValue) {
        alert('No Partner bookings cannot be invoiced as partner invoices');
        return;
      }
      if (this.partnerReport.selectedBookings.length === 0) {
        alert('Please select at least one booking');
        return;
      }

      const partner = this.getSelectedPartner();
      const partnerName = partner ? partner.name : 'this partner';
      const bookingCount = this.partnerReport.selectedBookings.length;
      const usesSharedInvoice = Boolean(partner?.bulk_partner_invoices_enabled);
      if (
        !confirm(
          usesSharedInvoice
            ? `Create partner invoice bundle for ${partnerName}?\n\nThis will create 1 shared invoice for ${bookingCount} booking${
                bookingCount > 1 ? 's' : ''
              } and generate a downloadable bundle.`
            : `Create partner invoice bundle for ${partnerName}?\n\nThis will create ${bookingCount} individual invoice${
                bookingCount > 1 ? 's' : ''
              } and generate a downloadable bundle.`,
        )
      ) {
        return;
      }

      try {
        const response = await fetch('/reports/api/bulk-invoice/', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': this.getCsrfToken(),
          },
          body: JSON.stringify({
            booking_ids: this.partnerReport.selectedBookings,
            invoice_type: this.partnerReport.invoiceType || 'simple',
          }),
        });
        const result = await response.json();
        if (response.ok && result.task_id) {
          this.partnerReport.bulkInvoiceTaskId = result.task_id;
          if (result.invoice_type) {
            this.partnerReport.invoiceType = result.invoice_type;
          }
          this.partnerReport.bundleTaskId = null;
          this.partnerReport.bundleInvoiceType = null;
          this.partnerReport.bundleStatus = null;
          this.partnerReport.selectedBookings = [];
          this.fetchPartnerReport();
          this.pollBulkInvoiceStatus(result.task_id);
        } else {
          alert(
            'Error: ' +
              (result.error || 'Failed to create partner invoice bundle'),
          );
        }
      } catch (error) {
        alert('Error creating partner invoice bundle: ' + error.message);
      }
    },

    async pollBulkInvoiceStatus(taskId) {
      const maxAttempts = 120;
      let attempts = 0;

      const checkStatus = async () => {
        try {
          const response = await fetch(`/invoicexpress/task-status/${taskId}/`);
          const data = await response.json();

          if (data.status === 'completed') {
            if (data.success && data.bundle_task_id) {
              this.partnerReport.bundleTaskId = data.bundle_task_id;
              this.partnerReport.bundleInvoiceType =
                this.partnerReport.invoiceType || 'simple';
              this.pollBundleStatus(data.bundle_task_id);
            } else {
              alert(
                'Invoice creation completed with errors: ' +
                  (data.error || 'Unknown error'),
              );
            }
            this.partnerReport.bulkInvoiceTaskId = null;
            this.fetchPartnerReport();
            return;
          }

          if (data.status === 'failed') {
            alert(
              'Invoice creation failed: ' + (data.error || 'Unknown error'),
            );
            this.partnerReport.bulkInvoiceTaskId = null;
            return;
          }

          attempts += 1;
          if (attempts < maxAttempts) {
            setTimeout(checkStatus, 5000);
          } else {
            alert(
              'Invoice creation is taking longer than expected. Please check back later.',
            );
            this.partnerReport.bulkInvoiceTaskId = null;
          }
        } catch (error) {
          console.error('Error polling bulk invoice status:', error);
          attempts += 1;
          if (attempts < maxAttempts) {
            setTimeout(checkStatus, 5000);
          }
        }
      };

      checkStatus();
    },

    async pollBundleStatus(taskId) {
      const maxAttempts = 240;
      let attempts = 0;

      const checkStatus = async () => {
        try {
          const response = await fetch(`/invoicexpress/task-status/${taskId}/`);
          const data = await response.json();
          this.partnerReport.bundleStatus = data;

          if (data.status === 'completed') {
            if (data.success) {
              this.partnerReport.bundleStatus = data;
              this.fetchPartnerReport();
            } else {
              alert(
                'Bundle generation failed: ' + (data.error || 'Unknown error'),
              );
              this.partnerReport.bundleStatus = null;
            }

            if (this.partnerReport.bundlePollingInterval) {
              clearInterval(this.partnerReport.bundlePollingInterval);
              this.partnerReport.bundlePollingInterval = null;
            }
            return;
          }

          if (data.status === 'failed') {
            alert(
              'Bundle generation failed: ' + (data.error || 'Unknown error'),
            );
            this.partnerReport.bundleStatus = null;
            if (this.partnerReport.bundlePollingInterval) {
              clearInterval(this.partnerReport.bundlePollingInterval);
              this.partnerReport.bundlePollingInterval = null;
            }
            return;
          }

          attempts += 1;
          if (attempts < maxAttempts) {
            setTimeout(checkStatus, 5000);
          } else {
            alert(
              'Bundle generation is taking longer than expected. Please check back later.',
            );
            this.partnerReport.bundleStatus = null;
            if (this.partnerReport.bundlePollingInterval) {
              clearInterval(this.partnerReport.bundlePollingInterval);
              this.partnerReport.bundlePollingInterval = null;
            }
          }
        } catch (error) {
          console.error('Error polling bundle status:', error);
          attempts += 1;
          if (attempts < maxAttempts) {
            setTimeout(checkStatus, 5000);
          }
        }
      };

      checkStatus();
    },

    async downloadBundle() {
      if (!this.partnerReport.bundleTaskId) {
        alert('No bundle available for download');
        return;
      }

      const invoiceType =
        this.partnerReport.bundleInvoiceType ||
        this.partnerReport.invoiceType ||
        'simple';
      const url = `/reports/api/bulk-invoice-bundle/${this.partnerReport.bundleTaskId}/?invoice_type=${invoiceType}`;

      try {
        const response = await fetch(url, { credentials: 'same-origin' });
        if (!response.ok) {
          let message =
            'Bundle is not ready yet. Please wait for generation to finish and try again.';
          try {
            const data = await response.json();
            if (data.error) {
              message = data.error;
            }
          } catch (parseError) {
            console.warn(
              'Could not parse bundle download error response',
              parseError,
            );
          }
          alert(message);
          return;
        }

        const blob = await response.blob();
        const disposition = response.headers.get('Content-Disposition') || '';
        const match = disposition.match(/filename="([^"]+)"/);
        const filename = match ? match[1] : 'partner_invoices.zip';
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(link.href);
      } catch (error) {
        console.error('Error downloading bundle:', error);
        alert('Error downloading bundle: ' + error.message);
      }
    },

    reportsCurrencySymbol(currency) {
      const code = currency || 'EUR';
      try {
        return (
          new Intl.NumberFormat(undefined, {
            style: 'currency',
            currency: code,
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          })
            .formatToParts(0)
            .find((part) => part.type === 'currency')?.value || code
        );
      } catch (error) {
        return { EUR: '€', USD: '$', GBP: '£' }[code] || code;
      }
    },

    reportsFormatCurrency(amount, currency) {
      const code = currency || 'EUR';
      const numAmount = parseFloat(amount);
      const value = Number.isNaN(numAmount) ? 0 : numAmount;
      try {
        return new Intl.NumberFormat(undefined, {
          style: 'currency',
          currency: code,
          currencyDisplay: 'narrowSymbol',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(value);
      } catch (error) {
        return `${this.reportsCurrencySymbol(code)}${value.toFixed(2)}`;
      }
    },

    reportsFormatDate(dateStr) {
      if (!dateStr) return '';
      const date = parseDate(dateStr);
      if (!date) return dateStr;
      return date.toLocaleDateString('en-GB');
    },

    reportsStatusDisplay(status) {
      return this.reportsSTATUS_DISPLAY[status] || status || '-';
    },

    formatCurrency(amount, currency) {
      return this.reportsFormatCurrency(amount, currency);
    },

    formatDate(dateStr) {
      return this.reportsFormatDate(dateStr);
    },

    statusDisplay(status) {
      return this.reportsStatusDisplay(status);
    },
  });
}
